import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { FiEye } from "react-icons/fi";
import { FiEyeOff } from "react-icons/fi";

import CustomInput from "../Form/Input";
import CustomButton from "../Form/CustomButtonWithHover";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import { useThunk } from "../../hooks/useThunk";
import { closeSignInModal, signin, startDataRefresh } from "../../store";
import showToast from "../../utils/toastNotifications";
import { useDispatch, useSelector } from "react-redux";
import { roles } from "../../constants/commonConstants";

const SignInForm = ({ isModel = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  const [doSignin, isSigninIn, signinError] = useThunk(signin);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm(
    {
      email: "",
      password: "",
      rememberMe: false,
    },
    {
      email: true,
      password: true,
      rememberMe: false,
    },
    {
      email: COMMON_FIELD_TYPES.EMAIL_OR_PHONE,
      password: "required",
      rememberMe: COMMON_FIELD_TYPES.BOOLEAN_FIELD,
    }
  );

  const handleSubmit = async (e) => {
    // console.log("submitting form");
    e.preventDefault();

    setTouched({
      email: true,
      password: true,
      rememberMe: true,
    });

    const { rememberMe, ...otherData } = formData;
    if (validateForm()) {
      const result = await doSignin(otherData);
      if (result?.success) {
        showToast("success", "Login Successful");
        // Start the data refresh process
        if (isModel) {
          dispatch(startDataRefresh());
        }
        localStorage.setItem("token", result?.response?.data?.token);

        if (!isModel) {
          // console.log("Navigating to landlord overview page");
          // navigate("/landlords/overview", { replace: true });
        } else {
          // Instead of reloading, close the modal and force component updates
          dispatch(closeSignInModal());

          // Force pending API requests to cancel and retry with new auth token
          // This could be a custom event that your API interceptors listen for
          window.dispatchEvent(new CustomEvent("auth-state-changed"));
        }
      } else {
        showToast("error", result?.error?.message);
      }
    }
  };

  return (
    <div
      className={`relative w-full  max-w-[512px] my-auto flex flex-col ${
        isModel ? "w-[440px]" : "w-full"
      } `}
    >
      <h1 className="text-[20px] font-semibold sm:text-[30px] text-[#4772F1] mt-[-10px] font-alexandria">
        Admin Login{" "}
      </h1>

      <form className="">
        <div className="flex flex-col gap-3 mt-[40px]">
          <CustomInput
            label={"E-mail Address"}
            type="text"
            name="email"
            value={formData.email}
            error={touched.email && errors.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            onBlur={() => handleBlur("email", formData.email)}
            onFocus={() => clearError("email")}
            placeholder="Enter your email address"
            className="w-full h-[54px] px-3 sm:text-[16px] rounded-lg border border-gray-300 bg-primary-light/30 text-gray-600"
            labelStyle="font-alexandria font-medium  sm:text-[16px] text-dark tracking-tightest"
            isRequired={true}
          />

        <div className="relative">
          <CustomInput
            label="Password"
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            error={touched.password && errors.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            onBlur={() => handleBlur("password", formData.password)}
            onFocus={() => clearError("password")}
            placeholder="Enter your password"
            className="w-full h-[54px] px-3 sm:text-[16px] rounded-lg border border-gray-300 bg-primary-light/30 text-gray-600"
            labelStyle="font-alexandria font-medium sm:text-[16px] text-dark tracking-tightest"
            isRequired={true}
          />
            <span
              className="absolute right-3 top-14 transform -translate-y-1/2 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <FiEye />
              ) : (
                <FiEyeOff />
              )}
            </span>
            {touched.password && !formData.password && (
              <span className="text-[#E31919] text-[13px] font-sans absolute top-[82px]">
                This field is required
              </span>
            )}
          </div>
        </div>

        <div className="flex justify-end  w-full mt-[6px] xs:mt-3">
          <Link
            to="/forgot-password"
            className="font-medium font-alexandria underline text-[#4772F1] text-[12px] sm:text-[16px]"
          >
            Forgot Password?
          </Link>
        </div>

        <div
          className={` flex flex-col gap-2 ${
            isModel ? "mt-[30px] md:mt-[60px]" : "mt-[30px] lg:mt-[70px]"
          } `}
        >
          <CustomButton
            type="submit"
            className="h-[50px] sm:h-[60px] bg-gradient-to-b from-[#4772F1] to-[#873FE2] text-[#F7FAFC] font-alexandria font-bold text-[20px] leading-[28px] rounded-xl"
            buttonText="Login"
            onClick={handleSubmit}
            loading={isSigninIn}
            disabled={isSigninIn}
          />
        </div>
      </form>
    </div>
  );
};

export default SignInForm;
